import { AdminApiError, type AdminClient } from '@apx/admin-sdk';
import {
  baccaratMultipliersSchema,
  type BaccaratMultipliersOverride,
} from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { showToast } from '../toast.js';
import { classifyReturn } from './inheritance.js';

export interface BaccaratMultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Whether the current scope is the provider baseline (vs. a specific integrator override). */
  isProviderScope: boolean;
}

/** Basis-point scale: returnBps / BPS_SCALE = total-return multiplier. */
const BPS_SCALE = 10000;

// Ordered bet cells — mirrors BAC_BET_TYPES in @apx/game-baccarat. Kept local to
// avoid an engine→games dependency in the admin bundle. Keep in sync if cells change.
type BacReturnKey =
  | 'BAC_PLAYER'
  | 'BAC_BANKER'
  | 'BAC_TIE'
  | 'BAC_PLAYER_PAIR'
  | 'BAC_BANKER_PAIR';

const RETURN_KEYS: BacReturnKey[] = [
  'BAC_PLAYER',
  'BAC_BANKER',
  'BAC_TIE',
  'BAC_PLAYER_PAIR',
  'BAC_BANKER_PAIR',
];

/**
 * Built-in total-return bps defaults — mirrors DEFAULT_BAC_RETURN_BPS in
 * @apx/game-baccarat.
 *
 * UNGUARDED: nothing imports the package constant to check this against, so if
 * the paytable moves there, these numbers silently keep advertising the old one
 * ("default N bps" hints, and the values seeded when no override exists). Update
 * both together. The sicbo/dragontiger tables next door ARE covered by
 * `multipliers-defaults.test.ts`; add a baccarat block there if you want the
 * same guard — it needs `@apx/game-baccarat` as a devDependency.
 */
export const BAC_DEFAULT_RETURNS: Record<BacReturnKey, number> = {
  BAC_PLAYER: 20000,
  BAC_BANKER: 19500,
  BAC_TIE: 90000,
  BAC_PLAYER_PAIR: 120000,
  BAC_BANKER_PAIR: 120000,
};

/** Mirrors DEFAULT_TIE_PUSH_BPS — 10000 bps = stake back, the standard push. */
export const BAC_DEFAULT_TIE_PUSH_BPS = 10000;

/**
 * EXACT win probabilities under the infinite-shoe model, as integer counts over
 * the 13^6 rank lattice (the same figures `outcomeProbabilities()` enumerates in
 * @apx/game-baccarat and GAME_DESIGN_DOC §4.1 states). Baccarat has NO RNG-weight
 * lever — the drawing rules are fixed — so these are constants, not a live
 * surface, and every cell's RTP is affine in its own multiplier:
 *
 *     RTP = P(win) × returnBps/10000 + P(tie) × tiePushBps/10000   (mains only)
 *
 * which is why this editor can show the exact resulting RTP as you type, with no
 * simulation and no round-trip.
 */
const LATTICE = 13 ** 6; // 4_826_809
const P_PLAYER = 2_153_464 / LATTICE; // 0.4461465
const P_BANKER = 2_212_744 / LATTICE; // 0.4584279
const P_TIE = 460_601 / LATTICE; //      0.0954256
const P_PAIR = 1 / 13; //                0.0769231 — rank-level, suit is irrelevant

/** Per-cell display copy plus the two terms of its RTP. */
const CELL: Record<
  BacReturnKey,
  { label: string; note: string; winProb: number; /** Mains push on a tie. */ hasPush: boolean }
> = {
  BAC_PLAYER: {
    label: 'Player',
    note: 'Standard 1:1 — 20000 bps.',
    winProb: P_PLAYER,
    hasPush: true,
  },
  BAC_BANKER: {
    label: 'Banker',
    note: 'Standard 1:1 less 5% commission — 19500 bps. The commission IS this multiplier; there is no separate ledger line.',
    winProb: P_BANKER,
    hasPush: true,
  },
  BAC_TIE: {
    label: 'Tie',
    note: 'Standard 8:1 — 90000 bps. 9:1 (100000) is the other common house rule.',
    winProb: P_TIE,
    hasPush: false,
  },
  BAC_PLAYER_PAIR: {
    label: 'Player Pair',
    note: 'Standard 11:1 — 120000 bps. Settles on the first two cards, independent of the main result.',
    winProb: P_PAIR,
    hasPush: false,
  },
  BAC_BANKER_PAIR: {
    label: 'Banker Pair',
    note: 'Standard 11:1 — 120000 bps. Settles on the first two cards, independent of the main result.',
    winProb: P_PAIR,
    hasPush: false,
  },
};

const DEFAULT_VALUE: BaccaratMultipliersOverride = {
  version: 1,
  returns: { ...BAC_DEFAULT_RETURNS },
  tiePushBps: BAC_DEFAULT_TIE_PUSH_BPS,
} as BaccaratMultipliersOverride;

/** Exact RTP for one cell — affine in its own multiplier. */
function cellRtp(key: BacReturnKey, returnBps: number, tiePushBps: number): number {
  const c = CELL[key];
  const push = c.hasPush ? P_TIE * (tiePushBps / BPS_SCALE) : 0;
  return c.winProb * (returnBps / BPS_SCALE) + push;
}

function rtpColor(rtp: number): string {
  if (rtp > 1.0) return 'var(--error)';
  if (rtp > 0.97) return 'var(--warn)';
  return 'var(--success)';
}

/** Human hint: total-return bps → "2.00× return (1.00:1)". */
function returnHint(bps: number): string {
  const ret = bps / BPS_SCALE;
  const odds = (bps - BPS_SCALE) / BPS_SCALE;
  return `${ret.toFixed(2)}× return (${odds.toFixed(2)}:1)`;
}

function rowHtml(key: BacReturnKey, isIntegratorScope: boolean): string {
  const tagCell = isIntegratorScope
    ? `<td data-tag="${key}" class="muted" style="min-width:80px">—</td>`
    : `<td class="muted" data-default="${key}">default ${BAC_DEFAULT_RETURNS[key]} bps</td>`;
  const revertCell = isIntegratorScope
    ? `<td><button data-revert="${key}" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button></td>`
    : `<td></td>`;
  return `<tr data-row="${key}" data-source="inherited">
    <td>
      <strong>${CELL[key].label}</strong>
      <div class="muted" style="font-size:12px">${CELL[key].note}</div>
    </td>
    <td><input data-bet="${key}" type="number" min="0" max="100000000" step="1" style="width:110px" /></td>
    <td class="muted" data-hint="${key}">—</td>
    <td class="muted" style="white-space:nowrap">${(CELL[key].winProb * 100).toFixed(4)}%</td>
    <td data-rtp="${key}" style="white-space:nowrap">—</td>
    ${tagCell}
    ${revertCell}
  </tr>`;
}

/**
 * Baccarat payout-multiplier editor. One total-return-bps input per bet cell
 * (`returns`), plus the `tiePushBps` lever — what a Player/Banker bet returns
 * when the coup ties (10000 = stake back, the standard push). Values are
 * total-return basis points: 20000 = 2.00× return = 1:1 win.
 *
 * These multipliers are baccarat's ONLY tunable RTP surface — the card draw is
 * uniform and the drawing rules are fixed, so there is no RNG-weight lever. Each
 * cell's RTP is therefore exact and is shown live as you type.
 *
 * OPERATOR NOTE: the shipped table is the conventional one (1:1 / 0.95:1 / 8:1 /
 * 11:1). Players recognise those numbers, so prefer moving one cell deliberately
 * over letting the target-RTP solver rewrite the whole board.
 *
 * In integrator scope: rows show inherited/override tags and per-row revert, and
 * readForm() submits only the override keys. In provider scope: the full table,
 * no inheritance UI. Changes ride the two-admin approval flow.
 */
export async function renderBaccaratMultipliersPage(
  root: HTMLElement,
  deps: BaccaratMultipliersPageDeps,
): Promise<void> {
  const isIntegratorScope = !deps.isProviderScope;

  const resetAllBtn = isIntegratorScope
    ? `<button id="bm-reset-all" class="ghost">Reset all to provider default</button>`
    : '';

  const pushTagHtml = isIntegratorScope
    ? `<span id="bm-push-tag" style="font-size:12px;font-weight:600;color:var(--text-dim)">—</span>
       <button id="bm-push-revert" class="ghost" style="display:none;padding:2px 6px;font-size:12px">↺ revert</button>`
    : '';

  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — Baccarat</h1>
    <p class="page-sub">Total-return per unit stake in basis points: <code>20000 = 2.00× return = 1:1 win</code>. The 5% banker commission is baked into the Banker cell (19500), not charged separately. Changes go through two-admin approval.</p>

    <div class="card">
      <h2>Tie push</h2>
      <div class="form-grid">
        <label>tiePushBps
          <input id="bm-push" type="number" min="0" max="100000000" step="1" />
          <span class="muted" id="bm-push-hint">—</span>
          ${pushTagHtml}
        </label>
      </div>
      <p class="muted" style="margin-top:8px">What a <strong>Player</strong> or <strong>Banker</strong> bet returns when the coup ties. <code>10000 = 1.00×</code> — stake back, the standard push. Set it to 0 and a tie becomes a loss on the mains, dropping both cells by ${(P_TIE * 100).toFixed(2)} points of RTP. The Tie and pair cells have no push term.</p>
    </div>

    <div class="card">
      <h2>Per-cell return (bps)</h2>
      <table>
        <thead><tr><th>Bet cell</th><th>Return (bps)</th><th>Multiple</th><th>P(win)</th><th>RTP</th>${isIntegratorScope ? '<th>Source</th><th></th>' : '<th></th><th></th>'}</tr></thead>
        <tbody id="bm-rows">${RETURN_KEYS.map((k) => rowHtml(k, isIntegratorScope)).join('')}</tbody>
      </table>
      <p class="muted" id="bm-headline" style="margin-top:10px">—</p>
      <div class="row" style="margin-top:14px">
        <button id="bm-reset" class="ghost">Reset to defaults</button>
        ${resetAllBtn}
        <button id="bm-save" class="primary">Propose change</button>
        <span id="bm-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="bm-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#bm-rows')!;
  const pushEl = root.querySelector<HTMLInputElement>('#bm-push')!;
  const pushHintEl = root.querySelector<HTMLElement>('#bm-push-hint')!;
  const pushTagEl = root.querySelector<HTMLElement>('#bm-push-tag');
  const pushRevertEl = root.querySelector<HTMLButtonElement>('#bm-push-revert');
  const headlineEl = root.querySelector<HTMLElement>('#bm-headline')!;
  const msgEl = root.querySelector<HTMLElement>('#bm-msg')!;

  // Per-row inherited values for revert (integrator scope only).
  const inheritedValues = new Map<BacReturnKey, number>();
  let inheritedPushBps = BAC_DEFAULT_TIE_PUSH_BPS;
  let pushIsOverride = false;

  const inputs = new Map<BacReturnKey, HTMLInputElement>();
  for (const key of RETURN_KEYS) {
    const el = rowsEl.querySelector<HTMLInputElement>(`input[data-bet="${key}"]`);
    if (el) inputs.set(key, el);
  }

  /**
   * Repaint every derived figure. The tie push feeds both the Player and Banker
   * RTP, so one recompute covers every input rather than a handler per field.
   */
  function refreshDerived(): void {
    const push = Number(pushEl.value) || 0;
    pushHintEl.textContent = `${(push / BPS_SCALE).toFixed(2)}× stake returned on a tie`;
    let bestKey: BacReturnKey = RETURN_KEYS[0]!;
    let best = -Infinity;
    for (const key of RETURN_KEYS) {
      const bps = Number(inputs.get(key)?.value) || 0;
      const hintEl = rowsEl.querySelector<HTMLElement>(`[data-hint="${key}"]`);
      if (hintEl) hintEl.textContent = returnHint(bps);
      const rtp = cellRtp(key, bps, push);
      const rtpEl = rowsEl.querySelector<HTMLElement>(`[data-rtp="${key}"]`);
      if (rtpEl) {
        const over = rtp > 1 ? ' <span style="color:var(--error)">over 100%!</span>' : '';
        rtpEl.innerHTML = `<strong style="color:${rtpColor(rtp)}">${(rtp * 100).toFixed(2)}%</strong>${over}`;
      }
      if (rtp > best) {
        best = rtp;
        bestKey = key;
      }
    }
    // Headline RTP is the BEST cell on the board — the honest ceiling on player
    // return, and what the RTP hero above reports. Matches headlineRtp() in
    // @apx/game-baccarat, which also takes the max rather than an average over
    // cells nobody is obliged to play.
    headlineEl.innerHTML =
      `Headline RTP (best cell on the board): <strong>${CELL[bestKey].label}</strong> at ` +
      `<strong style="color:${rtpColor(best)}">${(best * 100).toFixed(2)}%</strong> — house edge ` +
      `<strong>${((1 - best) * 100).toFixed(2)}%</strong>.`;
  }

  /** Mark a row as override (integrator scope only). */
  function markRowOverride(key: BacReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'override';
    if (tagEl) {
      tagEl.textContent = 'override';
      tagEl.style.color = 'var(--accent)';
      tagEl.style.fontWeight = '600';
    }
    if (revertEl) revertEl.style.display = '';
  }

  /** Mark a row as inherited (integrator scope only). */
  function markRowInherited(key: BacReturnKey): void {
    const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
    const tagEl = rowsEl.querySelector<HTMLElement>(`[data-tag="${key}"]`);
    const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
    if (row) row.dataset.source = 'inherited';
    if (tagEl) {
      tagEl.textContent = 'inherited';
      tagEl.style.color = 'var(--text-dim)';
      tagEl.style.fontWeight = '';
    }
    if (revertEl) revertEl.style.display = 'none';
  }

  function markPushOverride(): void {
    pushIsOverride = true;
    if (pushTagEl) {
      pushTagEl.textContent = 'override';
      pushTagEl.style.color = 'var(--accent)';
      pushTagEl.style.fontWeight = '600';
    }
    if (pushRevertEl) pushRevertEl.style.display = '';
  }

  function markPushInherited(): void {
    pushIsOverride = false;
    if (pushTagEl) {
      pushTagEl.textContent = 'inherited';
      pushTagEl.style.color = 'var(--text-dim)';
      pushTagEl.style.fontWeight = '';
    }
    if (pushRevertEl) pushRevertEl.style.display = 'none';
  }

  /** Seed rows from a full override value (provider scope or reset). */
  function fillForm(value: BaccaratMultipliersOverride): void {
    const returns = (value.returns ?? {}) as Record<string, number>;
    for (const [key, input] of inputs) {
      input.value = String(returns[key] ?? BAC_DEFAULT_RETURNS[key]);
    }
    pushEl.value = String(value.tiePushBps ?? BAC_DEFAULT_TIE_PUSH_BPS);
    refreshDerived();
  }

  /** Seed rows using inheritance classification (integrator scope). */
  function fillFormInherited(
    ownReturns: Record<string, number> | undefined,
    providerReturns: Record<string, number> | undefined,
    ownPushBps: number | undefined,
    providerPushBps: number | undefined,
  ): void {
    for (const [key, input] of inputs) {
      const classified = classifyReturn(key, ownReturns, providerReturns, BAC_DEFAULT_RETURNS[key]);
      input.value = String(classified.value);
      const inheritedVal =
        providerReturns && Object.prototype.hasOwnProperty.call(providerReturns, key)
          ? providerReturns[key]!
          : BAC_DEFAULT_RETURNS[key];
      inheritedValues.set(key, inheritedVal);
      if (classified.source === 'override') markRowOverride(key);
      else markRowInherited(key);
    }

    const effectivePush =
      ownPushBps !== undefined
        ? ownPushBps
        : providerPushBps !== undefined
          ? providerPushBps
          : BAC_DEFAULT_TIE_PUSH_BPS;
    inheritedPushBps = providerPushBps !== undefined ? providerPushBps : BAC_DEFAULT_TIE_PUSH_BPS;
    pushEl.value = String(effectivePush);
    if (ownPushBps !== undefined) markPushOverride();
    else markPushInherited();
    refreshDerived();
  }

  /** Build the propose payload. In integrator scope, only the override keys. */
  function readForm(): BaccaratMultipliersOverride {
    if (deps.isProviderScope) {
      const returns: Record<string, number> = {};
      for (const [key, input] of inputs) returns[key] = Math.round(Number(input.value) || 0);
      return {
        version: 1,
        returns,
        tiePushBps: Math.round(Number(pushEl.value) || 0),
      } as BaccaratMultipliersOverride;
    }
    // Integrator scope: partial `returns` — only the cells marked override.
    // Unlisted cells fall back to the provider/baked default at merge time.
    const returns: Record<string, number> = {};
    for (const [key, input] of inputs) {
      const row = rowsEl.querySelector<HTMLElement>(`tr[data-row="${key}"]`);
      if (row?.dataset.source === 'override') {
        returns[key] = Math.round(Number(input.value) || 0);
      }
    }
    return {
      version: 1,
      returns,
      tiePushBps: pushIsOverride ? Math.round(Number(pushEl.value) || 0) : inheritedPushBps,
    } as BaccaratMultipliersOverride;
  }

  // Editing marks the field as an override (integrator scope) and always
  // repaints the derived figures.
  for (const [key, input] of inputs) {
    input.addEventListener('input', () => {
      if (isIntegratorScope) markRowOverride(key);
      refreshDerived();
    });
  }
  pushEl.addEventListener('input', () => {
    if (isIntegratorScope) markPushOverride();
    refreshDerived();
  });

  if (isIntegratorScope) {
    for (const key of RETURN_KEYS) {
      const revertEl = rowsEl.querySelector<HTMLButtonElement>(`button[data-revert="${key}"]`);
      revertEl?.addEventListener('click', () => {
        const inherited = inheritedValues.get(key) ?? BAC_DEFAULT_RETURNS[key];
        const input = inputs.get(key);
        if (input) input.value = String(inherited);
        markRowInherited(key);
        refreshDerived();
      });
    }

    pushRevertEl?.addEventListener('click', () => {
      pushEl.value = String(inheritedPushBps);
      markPushInherited();
      refreshDerived();
    });

    root.querySelector<HTMLButtonElement>('#bm-reset-all')?.addEventListener('click', () => {
      for (const [key, input] of inputs) {
        input.value = String(inheritedValues.get(key) ?? BAC_DEFAULT_RETURNS[key]);
        markRowInherited(key);
      }
      pushEl.value = String(inheritedPushBps);
      markPushInherited();
      refreshDerived();
      msgEl.textContent = 'All cells reset to provider/baked defaults (not saved).';
      msgEl.className = 'muted';
    });
  }

  /** Re-read the surface after a save/approve, respecting the current scope. */
  async function reload(): Promise<void> {
    if (isIntegratorScope) {
      const inh = await deps.client.getConfigInheritance(deps.integratorId, deps.gameId);
      const own = inh.ownOverride.multipliers as Partial<BaccaratMultipliersOverride> | undefined;
      const prov = inh.providerDefault.multipliers as
        | Partial<BaccaratMultipliersOverride>
        | undefined;
      fillFormInherited(
        own?.returns as Record<string, number> | undefined,
        prov?.returns as Record<string, number> | undefined,
        own?.tiePushBps,
        prov?.tiePushBps,
      );
      return;
    }
    const { value } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
    const stored = value as Partial<BaccaratMultipliersOverride> | null;
    fillForm(stored && stored.returns ? (stored as BaccaratMultipliersOverride) : DEFAULT_VALUE);
  }

  await reload();

  root.querySelector<HTMLButtonElement>('#bm-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    if (isIntegratorScope) {
      for (const key of RETURN_KEYS) markRowOverride(key);
      markPushOverride();
    }
    msgEl.textContent = 'Reset to module defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#bm-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = baccaratMultipliersSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors
        .map((e) => `${e.path.join('.')}: ${e.message}`)
        .join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data as BaccaratMultipliersOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#bm-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — payout multipliers now live.';
          msgEl.className = 'muted';
          await refreshPending();
          await reload();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(
            err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err),
            'error',
          );
        }
      });
    });
  }

  await refreshPending();
}
